import { useEffect, useRef, useState } from "react";

import { centsToEuro, euroToCents } from "#shared/helpers/prices";
import { NumberInput } from "#shared/view/designSystem/inputs/NumberInput";
import { TextInput } from "#shared/view/designSystem/inputs/TextInput";
import { TimeInput } from "#shared/view/designSystem/inputs/TimeInput";
import { assertUnreachable, TimeDisplayFormatSchema, TimeDisplayFormatType } from "shared";

type CommonProps = {
  value: number | string | null | undefined;
  formatDisplayValue?: (value: number | string | null | undefined) => string;
  onCommit: (value: number | string | null) => void;
  disabled?: boolean;
  dataTestId?: string;
};

type TimeProps = CommonProps & {
  timeDisplayFormat: TimeDisplayFormatType;
  suffix?: undefined;
  min?: number;
  type: "time";
};

type PriceProps = CommonProps & {
  timeDisplayFormat?: undefined;
  suffix: string;
  min?: undefined;
  type: "price";
};

type TextProps = CommonProps & {
  timeDisplayFormat?: undefined;
  suffix?: undefined;
  min?: undefined;
  type: "text";
};
type EditableProcessStepCellProps = TimeProps | PriceProps | TextProps;

const isTimeProps = (props: EditableProcessStepCellProps): props is TimeProps => {
  return props.type === "time";
};

const isPriceProps = (props: EditableProcessStepCellProps): props is PriceProps => {
  return props.type === "price";
};

export const EditableProcessStepCell = (props: EditableProcessStepCellProps) => {
  const { value: initialValue, formatDisplayValue, onCommit, disabled = false, dataTestId } = props;

  const [value, setValue] = useState<number | string | null | undefined>(initialValue);
  const [isEditing, setIsEditing] = useState(false);
  const committedRef = useRef(false);
  const latestValueRef = useRef(initialValue);

  useEffect(() => {
    setValue(initialValue);
    latestValueRef.current = initialValue;
  }, [initialValue]);

  const updateValue = (nextValue: number | string | null | undefined) => {
    latestValueRef.current = nextValue;
    setValue(nextValue);
  };

  const commit = (finalValue: number | string | null | undefined = value) => {
    if (committedRef.current) {
      return;
    }
    committedRef.current = true;
    setIsEditing(false);

    if (finalValue !== initialValue) {
      onCommit(finalValue ?? null);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      commit(latestValueRef.current);
      e.stopPropagation();
    }
    if (e.key === "Escape") {
      updateValue(initialValue);
      setIsEditing(false);
      committedRef.current = true;
      e.stopPropagation();
    }
  };

  if (!isEditing) {
    let displayValue = value === null || value === undefined ? "-" : String(value);

    if (formatDisplayValue) {
      displayValue = formatDisplayValue(value);
    }

    return (
      <span
        onClick={() => {
          if (disabled) {
            return;
          }
          committedRef.current = false;
          setIsEditing(true);
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !disabled) {
            committedRef.current = false;
            setIsEditing(true);
            e.stopPropagation();
          }
        }}
        style={{
          minWidth: "50px",
          minHeight: "28px",
          display: "flex",
          alignItems: "center",
          cursor: disabled ? "default" : "text",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        data-testid={dataTestId}
      >
        {displayValue}
      </span>
    );
  }

  if (isPriceProps(props)) {
    return (
      <NumberInput
        autoFocus
        value={value === null || value === undefined ? "" : centsToEuro(Number(value))}
        onChange={(e) => updateValue(euroToCents(e))}
        onBlur={() => commit(value)}
        onKeyDown={onKeyDown}
        suffix={props.suffix}
        min={0}
        decimalScale={2}
        w="100%"
        small
        data-testid={dataTestId}
      />
    );
  }

  if (isTimeProps(props)) {
    const { timeDisplayFormat, min } = props;
    switch (timeDisplayFormat) {
      case TimeDisplayFormatSchema.enum.HH_MM_SS:
        return (
          <TimeInput
            autoFocus
            value={value === null || value === undefined ? undefined : Number(value)}
            onChange={(minutes) => updateValue(minutes)}
            onBlur={(minutes) => {
              updateValue(minutes);
              commit(minutes);
            }}
            onKeyDown={onKeyDown}
            min={min}
            w="100%"
            small
            data-testid={dataTestId}
          />
        );
      case TimeDisplayFormatSchema.enum.DECIMAL_MINUTES:
        return (
          <NumberInput
            autoFocus
            value={value === null || value === undefined ? "" : Number(value)}
            onChange={(e) => updateValue(e)}
            onBlur={() => commit(value)}
            onKeyDown={onKeyDown}
            min={min}
            w="100%"
            decimalScale={2}
            small
            data-testid={dataTestId}
          />
        );
      case TimeDisplayFormatSchema.enum.DECIMAL_HOURS:
        return (
          <NumberInput
            autoFocus
            value={value === null || value === undefined ? "" : Number(value) / 60}
            onChange={(e) => updateValue(e * 60)}
            onBlur={() => commit(value)}
            onKeyDown={onKeyDown}
            min={min}
            w="100%"
            decimalScale={2}
            small
            data-testid={dataTestId}
          />
        );
      default: {
        assertUnreachable(timeDisplayFormat);
      }
    }
  }

  return (
    <TextInput
      autoFocus
      value={String(value ?? "")}
      onChange={(e) => updateValue(e.target.value)}
      onBlur={() => commit(value)}
      onKeyDown={onKeyDown}
      w="100%"
      data-testid={dataTestId}
    />
  );
};
